import moment from "moment";
import {
    ButtonsBloc,
    FGCheckboxInput,
    FGNumberInput,
    FieldGroup,
    FieldSet,
    InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import {useHistory} from "react-router";
import * as Yup from "yup";

import {BroodRequestApi, BroodRequestDto, ERequestState, RequestInfoDto} from "../../../../api";
import {CancelButton, FGSRSHDateMaskInput, SmallFormGenerator} from "../../../../components";
import {FGEuroMaskInput} from "../../../../components/formGenerator/FGEuroMaskInput";
import {ERoutes} from "../../../../ERoutes";
import {useApiService, useCrudApi, useTl} from "../../../../hooks";
import {ETLCodes} from "../../../../locales";

export interface IBroodRequestDeclarationProps {
    broodRequestId: number;
    editMode: boolean;
    requestInfo: RequestInfoDto;
}

export const BroodRequestDeclaration: React.FunctionComponent<IBroodRequestDeclarationProps> = ({
                                                                                                    broodRequestId,
                                                                                                    editMode,
                                                                                                    requestInfo,
                                                                                                }) => {
    const {t} = useTl();
    const api = useApiService(BroodRequestApi);
    const history = useHistory();

    /* ========== Getting Data ===========*/
    const {data, loading, saving, saveItem} = useCrudApi<BroodRequestDto>(
        React.useMemo(
            () => ({
                getApiFn: async (id = broodRequestId) => {
                    return await api.get(id);
                },
                saveApiFn: async (d: BroodRequestDto) => {
                    d.bornPuppies = true;
                    return await api.save(d);
                },
                onSavedRoute: (d: BroodRequestDto) => `${ERoutes.broodRequest}/${d.broodRequestId}/detail/edit`,
                deleteApiFn: (d: BroodRequestDto) => {
                },
                onDeletedRoute: () => ERoutes.broodRequest,
            }),
            [broodRequestId, api]
        ),
        [broodRequestId]
    );

    /* ========== Yup ===========*/
    const OtherFormSchema = React.useMemo(() => {
        return Yup.object().shape({
            birthDate: Yup.date()
                .nullable()
                .max(new Date(), t(ETLCodes.DateCannotBeFuture))
                .test("is-required", t(ETLCodes.Required), async function (value) {
                    if (requestInfo?.requestStateCode === ERequestState.GESTATION) {
                        return value !== null && value !== undefined;
                    }
                    return true;
                })
                .test(
                    "protusionDate-diff",
                    t(ETLCodes.ProtusionDateAndBirthDateMustBeBetween54And68Days),
                    async function (value) {
                        if (requestInfo?.requestStateCode === ERequestState.GESTATION && value) {
                            const diff = moment(value).diff(moment(this.parent.protrusionDate), "days");
                            return diff >= 54 && diff <= 68;
                        }
                        return true;
                    }
                ),
            numberOfPuppies: Yup.number().test("is-required", t(ETLCodes.Required), async function (value) {
                if (requestInfo?.requestStateCode === ERequestState.GESTATION) {
                    return value !== null && value !== undefined && value > 0;
                }
                return true;
            }),
        });
    }, [requestInfo, t]);

    return (
        <SmallFormGenerator
            initialValues={data}
            editMode={editMode}
            onSubmit={saveItem}
            validationSchema={OtherFormSchema}
            loading={loading}
            saving={saving}
            hideButtons={data?.request?.status === ERequestState.EXECUTED}
            minLabelWidth={400}
        >
            <FieldSet title={t(ETLCodes.PuppyData)}>
                <FieldGroup columns={2}>
                    <FGSRSHDateMaskInput
                        label={t(ETLCodes.BirthDate)}
                        name="birthDate"
                        readonly={requestInfo?.isRequestBlocked || data?.hasPuppies}
                        requiredMark
                    />
                    <FGNumberInput
                        label={t(ETLCodes.NumberOfPuppies)}
                        name="numberOfPuppies"
                        readonly={requestInfo?.isRequestBlocked || requestInfo?.requestStateCode !== ERequestState.GESTATION}
                        requiredMark
                    />
                    <FGEuroMaskInput name="price" label={t(ETLCodes.Price)} readonly={requestInfo?.isRequestBlocked}/>
                    <FGCheckboxInput name="numberOfPuppiesConfirmed" label={t(ETLCodes.NumberOfPuppiesConfirmed)}
                                     readonly/>
                    {data?.isBrcsRequest && (
                        <FGCheckboxInput
                            name="wantSrshPedigree"
                            label={t(ETLCodes.SrshPedigreeRequestedWarningWillBeBilled)}
                            readonly={requestInfo?.isRequestBlocked || requestInfo?.requestStateCode !== ERequestState.GESTATION}
                        />
                    )}
                    {data?.isBrcsRequest && (
                        <FGCheckboxInput name="srshPedigreeConfirmed" label={t(ETLCodes.SrshPedigreeConfirmed)}/>
                    )}
                    {!data?.isBrcsRequest && (
                        <FGCheckboxInput name="brcsPedigreeConfirmed" label={t(ETLCodes.BrcsPedigreeConfirmed)}/>
                    )}
                </FieldGroup>

                {data?.request?.status !== ERequestState.GESTATION && (
                    <InlineButtonContainer>
                        <div/>
                        <ButtonsBloc style={{float: "right"}}>
                            <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.broodRequest}`)}/>
                        </ButtonsBloc>
                    </InlineButtonContainer>
                )}
            </FieldSet>
        </SmallFormGenerator>
    );
};
